'use client'

import {
  ModalContextType,
  ModalPositionType,
  ModalType,
} from '@/context/modal-context/types'
import { useModal } from '@/hooks/use-modal'
import dynamic from 'next/dynamic'
import React from 'react'
import { ModalBackground } from '../../molecules/modal-background/modal-background'
import { ModalPane } from './modal-pane'

const LoginModal = dynamic(
  () => import('../login-modal/login-modal').then((mod) => mod.LoginModal),
  { ssr: false }
)
const SignUpModal = dynamic(
  () =>
    import('../register-modal/register-modal').then((mod) => mod.SignUpModal),
  { ssr: false }
)
const SignInModal = dynamic(
  () => import('../sign-in-modal/sign-in-modal').then((mod) => mod.SignInModal),
  { ssr: false }
)
const BannerCardModal = dynamic(
  () =>
    import('../../molecules/banner-card-modal/banner-card-modal').then(
      (mod) => mod.BannerCardModal
    ),
  { ssr: false }
)
const ConfirmationModal = dynamic(
  () =>
    import('../../molecules/confirmation-modal/confirmation-modal').then(
      (mod) => mod.ConfirmationModal
    ),
  { ssr: false }
)
const RealWeddingsModal = dynamic(
  () =>
    import('../../../v3/organisms/real-weddings-modal/real-weddings-modal').then(
      (mod) => mod.RealWeddingsModal
    ),
  { ssr: false }
)
const AuthorPageFormModal = dynamic(
  () =>
    import('@/components/v3/unique/admin/author-page-form/author-page-form-modal/author-page-form-modal').then(
      (mod) => mod.AuthorPageFormModal
    ),
  { ssr: false }
)
const SupplierOffersAndEventsModal = dynamic(
  () =>
    import('@/components/v3/unique/supplier/supplier-offers-and-events/supplier-offers-and-events-modal/supplier-offers-and-events-modal').then(
      (mod) => mod.SupplierOffersAndEventsModal
    ),
  { ssr: false }
)
const SupplierEnquiryFormModal = dynamic(
  () =>
    import('@/components/v3/unique/supplier/supplier-enquiry-form-modal/supplier-enquiry-form-modal').then(
      (mod) => mod.SupplierEnquiryFormModal
    ),
  { ssr: false }
)
const SupplierEnquiryFormSuccessModal = dynamic(
  () =>
    import('@/components/v3/unique/supplier/supplier-enquiry-form-success-modal/supplier-enquiry-form-success-modal').then(
      (mod) => mod.SupplierEnquiryFormSuccessModal
    ),
  { ssr: false }
)
const DatepickerModal = dynamic(
  () =>
    import('@/components/v3/organisms/datepicker-modal/datepicker-modal').then(
      (mod) => mod.DatepickerModal
    ),
  { ssr: false }
)
const LocationSelectModal = dynamic(
  () =>
    import('@/components/v3/molecules/location-select/location-select-modal').then(
      (mod) => mod.LocationSelectModal
    ),
  { ssr: false }
)
const SupplierCategoryFiltersModal = dynamic(
  () =>
    import('@/components/v3/unique/supplier/supplier-category-filters-modal/supplier-category-filters-modal').then(
      (mod) => mod.SupplierCategoryFiltersModal
    ),
  { ssr: false }
)

const GDPRDeleteModal = dynamic(
  () =>
    import('@/components/v3/molecules/gdpr-delete-modal/gdpr-delete-modal').then(
      (mod) => mod.GDPRDeleteModal
    ),
  { ssr: false }
)

const modalComponents: Partial<Record<ModalType, React.ComponentType<any>>> = {
  login: LoginModal,
  'sign-up': SignUpModal,
  'sign-in': SignInModal,
  'banner-card': BannerCardModal,
  confirmation: ConfirmationModal,
  'real-weddings-filters': RealWeddingsModal,
  'author-page-form': AuthorPageFormModal,
  'supplier-offers-and-events': SupplierOffersAndEventsModal,
  'supplier-enquiry-form': SupplierEnquiryFormModal,
  'supplier-enquiry-form-success': SupplierEnquiryFormSuccessModal,
  datepicker: DatepickerModal,
  'location-select': LocationSelectModal,
  'supplier-category-filters': SupplierCategoryFiltersModal,
  'gdpr-delete': GDPRDeleteModal,
}

const getModalContent = (
  modal: ModalContextType,
  position: ModalPositionType
) => {
  if (!modal.isOpen || modal.options.position !== position) {
    return null
  }

  const ModalComponent = modalComponents[modal.name]
  if (!ModalComponent) {
    return null
  }

  return <ModalComponent {...modal.options.modalProps} />
}

const modalPositions = [
  'top',
  'right',
  'bottom',
  'left',
] as Array<ModalPositionType>

export const Modal: React.FC = () => {
  const modal = useModal()

  return (
    <>
      <ModalBackground />
      {modalPositions.map((position) => (
        <ModalPane key={position} position={position}>
          {getModalContent(modal, position)}
        </ModalPane>
      ))}
    </>
  )
}
